'use client';

import {useEffect} from 'react';
import {useCoursePrice} from '@udemy-v2/react-course-pricing';
import {SubscriptionPlanContext, ConsumerSubscriptionPlan} from '@udemy-v2/subscription-graphql';
import {Course} from '@udemy-v2/browse-course';
import {getCourseOffers} from './course-seo.react-component';

export interface CourseSeoPricingProps {
    course: Course;
}

/**
 * Client-side component that hydrates pricing data into existing SEO schema
 * This is the most reliable approach for ISR compatibility:
 * 1. Server renders complete schema without pricing data
 * 2. Client updates existing DOM with pricing data
 * 3. No context or state management needed
 *
 * Note: Subscription context is currently hardcoded to undefined
 */
export function CourseSeoPricing({course}: CourseSeoPricingProps) {
    const coursePrice = useCoursePrice(Number(course.id));

    // Hardcode subscription context to undefined for now
    const subscriptionContext: SubscriptionPlanContext<ConsumerSubscriptionPlan> = undefined;

    useEffect(() => {
        // Skip if course is free (no pricing offers needed)
        if (course.isFree) {
            return;
        }

        // Only update if we have pricing data (subscription is hardcoded to undefined)
        if (!coursePrice) {
            return;
        }

        // Find the existing schema script element
        const schemaScript = document.querySelector(
            '#schema_markup script[type="application/ld+json"]',
        );
        if (!schemaScript) {
            return;
        }

        try {
            // Parse the existing schema
            const existingSchema = JSON.parse(schemaScript.textContent || '{}');

            // Update the course schema with pricing data
            if (existingSchema['@graph'] && existingSchema['@graph'][0]) {
                const courseSchema = existingSchema['@graph'][0];
                courseSchema.offers = getCourseOffers({
                    course,
                    coursePrice,
                    subscriptionContext,
                });

                // Update the script content with new pricing data
                const updatedSchemaString = JSON.stringify(existingSchema);
                schemaScript.textContent = updatedSchemaString;
            }
        } catch (error) {
            console.warn('Failed to update SEO schema with pricing data:', error);
        }
    }, [course, coursePrice, subscriptionContext]);

    // This component doesn't render anything visible - it only updates the schema
    return null;
}
